import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { CalendarRange, Pencil, Plus, Trash2, X } from "lucide-react";

const inputCls = "w-full bg-black border border-white/15 px-2 py-1.5 text-sm text-[#F5F2EB] placeholder-[#52525B] focus:outline-none focus:border-[#D4AF37]";
const pctLabel = (p) => `${p > 0 ? "+" : ""}${Number(p).toFixed(Number.isInteger(Number(p)) ? 0 : 1)}%`;
const fmt = (iso) => iso ? new Date(iso + "T12:00:00").toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" }) : "";
const STATUS = {
  active: "border-emerald-500/40 text-emerald-400",
  upcoming: "border-[#D4AF37]/40 text-[#D4AF37]",
  inactive: "border-white/15 text-[#71717A]",
  expired: "border-white/10 text-[#52525B]",
};
const EMPTY = { name: "", pct: 15, kind: "fixed", start_date: "", end_date: "", start_md: "", end_md: "", vehicle_ids: [], active: true, note: "" };

/** Dispatcher: temporary % rate changes by date window — auto-activate, auto-revert. */
export default function RateScheduler({ vehicles }) {
  const [rows, setRows] = useState(null);
  const [today, setToday] = useState("");
  const [editing, setEditing] = useState(null); // null | {…draft}
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/rate-schedules").then(r => { setRows(r.data.schedules); setToday(r.data.today); }).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const save = async () => {
    const d = editing;
    const body = { name: d.name, pct: parseFloat(d.pct), kind: d.kind, vehicle_ids: d.vehicle_ids, active: d.active, note: d.note || null };
    if (d.kind === "fixed") { body.start_date = d.start_date; body.end_date = d.end_date; }
    if (d.kind === "annual") { body.start_md = d.start_md; body.end_md = d.end_md; }
    if (Number.isNaN(body.pct)) { toast.error("Enter a percentage"); return; }
    setBusy(true);
    try {
      if (d.schedule_id) await api.put(`/rate-schedules/${d.schedule_id}`, body);
      else await api.post("/rate-schedules", body);
      toast.success(d.schedule_id ? "Schedule updated" : "Schedule created — it will activate automatically");
      setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save schedule"); }
    finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"?`)) return;
    await api.delete(`/rate-schedules/${s.schedule_id}`); toast.success("Schedule deleted"); load();
  };

  const toggle = async (s) => {
    const body = { name: s.name, pct: s.pct, kind: s.kind, start_date: s.start_date, end_date: s.end_date, start_md: s.start_md, end_md: s.end_md, vehicle_ids: s.vehicle_ids || [], active: s.active === false, note: s.note || null };
    await api.put(`/rate-schedules/${s.schedule_id}`, body); load();
  };

  const kindLabel = (s) => s.kind === "thanksgiving" ? "Wed before Thanksgiving → Sun after · every year"
    : s.kind === "annual" ? `${s.start_md} → ${s.end_md} · every year` : `${fmt(s.start_date)} → ${fmt(s.end_date)}`;

  return (
    <div className="mt-12 border border-white/10 bg-[#0A0A0A] p-6" data-testid="rate-scheduler">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="overline mb-1 flex items-center gap-2"><CalendarRange size={12}/> Rate scheduler</div>
          <h3 className="font-serif text-2xl">Scheduled rate changes</h3>
          <p className="text-xs text-[#A1A1AA] mt-1 max-w-2xl">
            Applies to trips whose <b className="text-[#F5F2EB]">pickup date</b> falls in the window — activates and reverts automatically, no manual action.
            Overlapping schedules use the higher percentage. Dispatchers can still override any individual reservation's price.
          </p>
        </div>
        <button data-testid="rate-schedule-new" onClick={() => setEditing({ ...EMPTY })} className="btn-gold !py-2 !px-4 text-[10px] inline-flex items-center gap-1.5"><Plus size={12}/> New schedule</button>
      </div>

      {editing && (
        <div className="border border-[#D4AF37]/40 bg-black/40 p-4 mb-5 space-y-3" data-testid="rate-schedule-editor">
          <div className="grid md:grid-cols-[1fr_120px_180px] gap-3">
            <label className="block"><span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A]">Name</span>
              <input data-testid="rs-name" className={inputCls} value={editing.name} onChange={e => setEditing(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Summerfest surge"/></label>
            <label className="block"><span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A]">Change %</span>
              <input data-testid="rs-pct" type="number" step="0.5" className={inputCls} value={editing.pct} onChange={e => setEditing(d => ({ ...d, pct: e.target.value }))}/></label>
            <label className="block"><span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A]">Repeats</span>
              <select data-testid="rs-kind" className={inputCls} value={editing.kind} onChange={e => setEditing(d => ({ ...d, kind: e.target.value }))}>
                <option value="fixed">One-off (specific dates)</option>
                <option value="annual">Every year (same dates)</option>
                <option value="thanksgiving">Thanksgiving weekend (auto)</option>
              </select></label>
          </div>
          {editing.kind === "fixed" && (
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <label className="block"><span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A]">Start date</span>
                <input data-testid="rs-start-date" type="date" className={inputCls} value={editing.start_date || ""} onChange={e => setEditing(d => ({ ...d, start_date: e.target.value }))}/></label>
              <label className="block"><span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A]">End date (inclusive)</span>
                <input data-testid="rs-end-date" type="date" className={inputCls} value={editing.end_date || ""} onChange={e => setEditing(d => ({ ...d, end_date: e.target.value }))}/></label>
            </div>
          )}
          {editing.kind === "annual" && (
            <div className="grid grid-cols-2 gap-3 max-w-md">
              <label className="block"><span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A]">Start (MM-DD)</span>
                <input data-testid="rs-start-md" className={inputCls} placeholder="12-23" value={editing.start_md || ""} onChange={e => setEditing(d => ({ ...d, start_md: e.target.value }))}/></label>
              <label className="block"><span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A]">End (MM-DD, inclusive)</span>
                <input data-testid="rs-end-md" className={inputCls} placeholder="12-26" value={editing.end_md || ""} onChange={e => setEditing(d => ({ ...d, end_md: e.target.value }))}/></label>
            </div>
          )}
          <div>
            <span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A]">Vehicles (none selected = whole fleet)</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {vehicles.map(v => {
                const on = editing.vehicle_ids.includes(v.vehicle_id);
                return <button key={v.vehicle_id} type="button" data-testid={`rs-vehicle-${v.vehicle_id}`}
                  onClick={() => setEditing(d => ({ ...d, vehicle_ids: on ? d.vehicle_ids.filter(x => x !== v.vehicle_id) : [...d.vehicle_ids, v.vehicle_id] }))}
                  className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.14em] border ${on ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/15 text-[#A1A1AA]"}`}>{v.name}</button>;
              })}
            </div>
          </div>
          <input data-testid="rs-note" className={inputCls} placeholder="Note (optional)" value={editing.note || ""} onChange={e => setEditing(d => ({ ...d, note: e.target.value }))} maxLength={200}/>
          <div className="flex gap-2">
            <button data-testid="rs-save" disabled={busy} onClick={save} className="btn-gold !py-1.5 !px-4 text-[10px] disabled:opacity-40">{busy ? "Saving…" : "Save schedule"}</button>
            <button data-testid="rs-cancel" onClick={() => setEditing(null)} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border border-white/15 text-[#A1A1AA] inline-flex items-center gap-1"><X size={11}/> Cancel</button>
          </div>
        </div>
      )}

      {rows === null && <div className="text-xs text-[#A1A1AA]">Loading…</div>}
      {rows?.length === 0 && <div className="text-sm text-[#A1A1AA] border border-white/10 p-6 text-center">No scheduled rate changes.</div>}
      <div className="space-y-2" data-testid="rate-schedule-list">
        {rows?.map(s => (
          <div key={s.schedule_id} data-testid={`rate-schedule-${s.schedule_id}`} className={`flex flex-wrap items-center gap-3 border border-white/10 p-3 ${s.active === false ? "opacity-50" : ""}`}>
            <span data-testid={`rs-status-${s.schedule_id}`} className={`text-[9px] uppercase tracking-[0.18em] border px-2 py-0.5 ${STATUS[s.status]}`}>{s.status}</span>
            <span className={`font-serif text-xl w-20 ${s.pct < 0 ? "text-emerald-400" : "text-[#D4AF37]"}`} data-testid={`rs-pct-${s.schedule_id}`}>{pctLabel(s.pct)}</span>
            <div className="flex-1 min-w-[200px]">
              <div className="text-sm text-[#F5F2EB]">{s.name} {s.default && <span className="text-[9px] uppercase tracking-[0.14em] text-[#71717A] ml-1">default</span>}</div>
              <div className="text-[11px] text-[#A1A1AA]">
                {kindLabel(s)}{s.next_start && s.status !== "expired" && <> · <span className="text-[#F5F2EB]">next: {fmt(s.next_start)} – {fmt(s.next_end)}</span></>}
                {s.vehicle_ids?.length ? ` · ${s.vehicle_ids.length} vehicle${s.vehicle_ids.length > 1 ? "s" : ""}` : " · whole fleet"}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button data-testid={`rs-toggle-${s.schedule_id}`} onClick={() => toggle(s)} className={`px-2.5 py-1.5 text-[10px] uppercase tracking-[0.16em] border ${s.active === false ? "border-emerald-500/40 text-emerald-400" : "border-white/15 text-[#A1A1AA]"}`}>{s.active === false ? "Enable" : "Disable"}</button>
              <button data-testid={`rs-edit-${s.schedule_id}`} onClick={() => setEditing({ ...EMPTY, ...s, vehicle_ids: s.vehicle_ids || [], note: s.note || "" })} aria-label="Edit" className="w-8 h-8 border border-white/15 text-[#A1A1AA] hover:text-[#D4AF37] flex items-center justify-center"><Pencil size={13}/></button>
              <button data-testid={`rs-delete-${s.schedule_id}`} onClick={() => remove(s)} aria-label="Delete" className="w-8 h-8 border border-red-500/30 text-red-400 hover:bg-red-500/10 flex items-center justify-center"><Trash2 size={13}/></button>
            </div>
          </div>
        ))}
      </div>
      {today && <div className="mt-3 text-[10px] text-[#71717A]">Business date today: {fmt(today)} · checks run every 10 minutes</div>}
    </div>
  );
}
